/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { McpSubscription } from './types';
import {
  fetchMcpSubscriptions,
  fetchMcpSubscriptionsByClient,
  cancelMcpSubscription,
} from './mcpSubscriptionService';
import McpSubscriptionFormDialog from './McpSubscriptionFormDialog';
import ConfirmDialog from '../clients/ConfirmDialog';
import { SnackBarMessage } from '../../../components/snackBar/snackBar';
import './mcp-subscriptions.scss';

const planColorMap: Record<string, 'default' | 'primary' | 'secondary'> = {
  BASIC: 'default',
  PRO: 'primary',
  ENTERPRISE: 'secondary',
};

const statusColorMap: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  CANCELLED: 'error',
};

const AdminMcpSubscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<McpSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<McpSubscription | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<McpSubscription | null>(null);
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false,
  });

  const loadSubscriptions = () => {
    setLoading(true);
    const clientId = Number(clientIdFilter);
    const request = clientId > 0
      ? fetchMcpSubscriptionsByClient(clientId)
      : fetchMcpSubscriptions();

    request
      .then((data) => {
        setSubscriptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to load subscriptions.',
          snackFlag: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterApply = () => {
    loadSubscriptions();
  };

  const handleCreate = () => {
    setEditingSubscription(null);
    setFormOpen(true);
  };

  const handleEdit = (subscription: McpSubscription) => {
    setEditingSubscription(subscription);
    setFormOpen(true);
  };

  const handleCancelClick = (subscription: McpSubscription) => {
    setSubscriptionToCancel(subscription);
    setConfirmOpen(true);
  };

  const handleCancelConfirm = () => {
    if (!subscriptionToCancel) return;
    cancelMcpSubscription(subscriptionToCancel.id)
      .then(() => {
        setMsgConfig({
          type: 'success',
          message: 'Subscription cancelled successfully.',
          snackFlag: true,
        });
        loadSubscriptions();
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to cancel subscription.',
          snackFlag: true,
        });
      })
      .finally(() => {
        setConfirmOpen(false);
        setSubscriptionToCancel(null);
      });
  };

  const handleFormSuccess = (message: string) => {
    setMsgConfig({
      type: 'success',
      message,
      snackFlag: true,
    });
    loadSubscriptions();
  };

  const handleFormError = (message: string) => {
    setMsgConfig({
      type: 'error',
      message,
      snackFlag: true,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'clientId',
      headerName: 'Client ID',
      width: 100,
    },
    {
      field: 'plan',
      headerName: 'Plan',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={planColorMap[params.value] || 'default'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={statusColorMap[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 130,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 130,
      renderCell: (params) => params.value || '—',
    },
    {
      field: 'maxToolCalls',
      headerName: 'Max Tool Calls',
      width: 140,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              aria-label="edit subscription"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status !== 'CANCELLED' && (
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleCancelClick(params.row)}
                aria-label="cancel subscription"
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box className="admin-mcp-subscriptions">
      <Box className="admin-mcp-subscriptions__header">
        <Typography variant="h5">MCP Subscription Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Add Subscription
        </Button>
      </Box>

      <Box className="admin-mcp-subscriptions__filter">
        <TextField
          label="Filter by Client ID"
          type="number"
          size="small"
          value={clientIdFilter}
          onChange={(e) => setClientIdFilter(e.target.value)}
          sx={{ width: 200 }}
        />
        <Button variant="outlined" onClick={handleFilterApply} sx={{ ml: 1 }}>
          Apply
        </Button>
      </Box>

      {loading ? (
        <Box className="admin-mcp-subscriptions__loading">
          <CircularProgress />
        </Box>
      ) : (
        <Box className="admin-mcp-subscriptions__grid">
          <DataGrid
            rows={subscriptions}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      )}

      <McpSubscriptionFormDialog
        open={formOpen}
        subscription={editingSubscription}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Cancel Subscription"
        message={`Are you sure you want to cancel subscription #${subscriptionToCancel?.id}? This action is idempotent — cancelling an already-cancelled subscription has no effect.`}
        onConfirm={handleCancelConfirm}
        onCancel={() => {
          setConfirmOpen(false);
          setSubscriptionToCancel(null);
        }}
      />

      {msgConfig.snackFlag && <SnackBarMessage snacBarValue={msgConfig} />}
    </Box>
  );
};

export default AdminMcpSubscriptions;
